/**
 * ContentBlockRenderer
 * 
 * Renders structured JSON content blocks into React elements.
 * Layout is LOCKED — CMS cannot control structure, spacing, or layout.
 * Only text content is dynamic.
 */

interface ContentBlock {
  type: "heading" | "paragraph" | "list" | "quote" | "image" | "callout" | "divider";
  level?: number; // for headings: 2 or 3
  text?: string;
  items?: string[];
  src?: string;
  alt?: string;
  caption?: string;
  ordered?: boolean;
}

interface ContentBlockRendererProps {
  blocks: ContentBlock[];
}

const ContentBlockRenderer = ({ blocks }: ContentBlockRendererProps) => {
  if (!blocks || blocks.length === 0) {
    return <p className="text-muted-foreground">No content available.</p>;
  }

  return (
    <div className="space-y-6">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "heading":
            if (block.level === 3) {
              return (
                <h3 key={i} className="text-xl font-semibold text-foreground tracking-tight mt-8 mb-3">
                  {block.text}
                </h3>
              );
            }
            return (
              <h2 key={i} className="text-2xl font-semibold text-foreground tracking-tight mt-10 mb-4">
                {block.text}
              </h2>
            );

          case "paragraph":
            return (
              <p key={i} className="text-muted-foreground leading-relaxed">
                {block.text}
              </p>
            );

          case "list":
            if (block.ordered) {
              return (
                <ol key={i} className="list-decimal list-inside space-y-2 text-muted-foreground pl-1">
                  {block.items?.map((item, j) => (
                    <li key={j} className="leading-relaxed">{item}</li>
                  ))}
                </ol>
              );
            }
            return (
              <ul key={i} className="list-disc list-inside space-y-2 text-muted-foreground pl-1">
                {block.items?.map((item, j) => (
                  <li key={j} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            );

          case "quote":
            return (
              <blockquote key={i} className="border-l-2 border-primary/30 pl-6 py-2 my-6">
                <p className="text-foreground italic leading-relaxed">{block.text}</p>
              </blockquote>
            );

          case "callout":
            return (
              <div key={i} className="p-5 bg-muted/30 border border-border rounded-sm my-6">
                <p className="text-sm text-foreground leading-relaxed">{block.text}</p>
              </div>
            );

          case "image":
            return (
              <figure key={i} className="my-8">
                <img
                  src={block.src}
                  alt={block.alt || ""}
                  className="w-full rounded-sm border border-border"
                  loading="lazy"
                />
                {block.caption && (
                  <figcaption className="text-xs text-muted-foreground mt-2 text-center">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );

          case "divider":
            return <hr key={i} className="border-border my-8" />;

          default:
            return null;
        }
      })}
    </div>
  );
};

export default ContentBlockRenderer;
